import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SlidersHorizontal } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { products, categories } from '../data/products';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';

export const Category = () => {
  const { categoryId } = useParams();
  const [sortBy, setSortBy] = useState('featured');
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const category = categories.find(c => c.id === categoryId);
  const categoryName = categoryId === 'all' ? 'All Collections' : category?.name || 'Products';

  const materials = ['Velvet', 'Leather', 'Wood', 'Marble', 'Fabric', 'Steel'];
  const ratings = [5, 4, 3];

  const filteredProducts = useMemo(() => {
    let result = categoryId === 'all' 
      ? products 
      : products.filter(p => p.category === categoryId);

    // Price filter
    result = result.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);

    // Rating filter
    if (selectedRatings.length > 0) {
      result = result.filter(p => selectedRatings.some(r => Math.floor(p.rating) >= r));
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        result = [...result].sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result = [...result].sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        result = [...result].sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        result = [...result].sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        break;
      default:
        break;
    }

    return result;
  }, [categoryId, sortBy, priceRange, selectedRatings]);

  const handleMaterialToggle = (material) => {
    setSelectedMaterials(prev =>
      prev.includes(material)
        ? prev.filter(m => m !== material)
        : [...prev, material]
    );
  };

  const handleRatingToggle = (rating) => {
    setSelectedRatings(prev =>
      prev.includes(rating)
        ? prev.filter(r => r !== rating)
        : [...prev, rating]
    );
  };

  const clearFilters = () => {
    setPriceRange([0, 10000]);
    setSelectedMaterials([]);
    setSelectedRatings([]);
  };

  const activeFilterCount = (priceRange[0] > 0 || priceRange[1] < 10000 ? 1 : 0) +
    selectedMaterials.length + selectedRatings.length;

  const FilterContent = () => (
    <div className="space-y-8">
      {/* Price Range */}
      <div>
        <h4 className="font-['Montserrat'] font-semibold text-xs uppercase tracking-wider text-[#2D4A3E] mb-4">
          Price Range
        </h4>
        <Slider
          value={priceRange}
          onValueChange={setPriceRange}
          min={0}
          max={10000}
          step={100}
          className="mb-2"
          data-testid="price-slider"
        />
        <div className="flex justify-between text-sm text-[#8B7355] font-['Montserrat']">
          <span>${priceRange[0].toLocaleString()}</span>
          <span>${priceRange[1].toLocaleString()}</span>
        </div>
      </div>

      {/* Materials */}
      <div>
        <h4 className="font-['Montserrat'] font-semibold text-xs uppercase tracking-wider text-[#2D4A3E] mb-4">
          Material
        </h4>
        <div className="space-y-3">
          {materials.map(material => (
            <div key={material} className="flex items-center gap-3">
              <Checkbox
                id={`material-${material}`}
                checked={selectedMaterials.includes(material)}
                onCheckedChange={() => handleMaterialToggle(material)}
                data-testid={`filter-material-${material.toLowerCase()}`}
              />
              <Label 
                htmlFor={`material-${material}`}
                className="font-['Montserrat'] text-sm text-[#5A6B5D] cursor-pointer"
              >
                {material}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Ratings */}
      <div>
        <h4 className="font-['Montserrat'] font-semibold text-xs uppercase tracking-wider text-[#2D4A3E] mb-4">
          Rating
        </h4>
        <div className="space-y-3">
          {ratings.map(rating => (
            <div key={rating} className="flex items-center gap-3">
              <Checkbox
                id={`rating-${rating}`}
                checked={selectedRatings.includes(rating)}
                onCheckedChange={() => handleRatingToggle(rating)}
                data-testid={`filter-rating-${rating}`}
              />
              <Label 
                htmlFor={`rating-${rating}`}
                className="font-['Montserrat'] text-sm text-[#5A6B5D] cursor-pointer flex items-center gap-1"
              >
                {rating}+ Stars
              </Label>
            </div>
          ))}
        </div>
      </div>

      {activeFilterCount > 0 && (
        <Button
          variant="outline"
          onClick={clearFilters}
          className="w-full rounded-sm border-[#E8E4DE] text-[#5A6B5D] hover:bg-[#FAF8F5] font-['Montserrat']"
          data-testid="clear-filters"
        >
          Clear All Filters
        </Button>
      )}
    </div>
  );

  return (
    <main className="pt-24 pb-24 bg-[#FAF8F5]" data-testid="category-page">
      {/* Category Header */}
      <section className="bg-[#2D4A3E] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white" data-testid="category-title">
              {categoryName}
            </h1>
            <p className="font-['Montserrat'] text-base text-[#A8B5AB] mt-4 max-w-2xl mx-auto">
              {category?.description || 'Discover our curated collection of premium furniture pieces'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 md:mb-12 pb-6 border-b border-[#E8E4DE]">
            <p className="font-['Montserrat'] text-sm text-[#8B7355]">
              Showing {filteredProducts.length} products
            </p>

            <div className="flex items-center gap-4">
              {/* Mobile Filter Button */}
              <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                <SheetTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="lg:hidden rounded-sm border-[#E8E4DE] flex items-center gap-2 font-['Montserrat']"
                    data-testid="mobile-filter-btn"
                  >
                    <SlidersHorizontal size={16} />
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="bg-[#C67D4E] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 bg-[#FAF8F5]">
                  <SheetHeader>
                    <SheetTitle className="font-['Cormorant_Garamond'] text-xl text-[#2D4A3E]">Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterContent />
                  </div>
                </SheetContent>
              </Sheet>

              {/* Sort Dropdown */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px] rounded-sm border-[#E8E4DE] font-['Montserrat'] text-sm" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="rating">Best Rated</SelectItem>
                  <SelectItem value="newest">New Arrivals</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-12">
            {/* Desktop Sidebar Filters */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-32 bg-white p-6 border border-[#E8E4DE]">
                <h3 className="font-['Cormorant_Garamond'] text-xl text-[#2D4A3E] mb-6">Filters</h3>
                <FilterContent />
              </div>
            </aside>

            {/* Product List - Single Column */}
            <div className="flex-1">
              {filteredProducts.length > 0 ? (
                <div className="space-y-6">
                  {filteredProducts.map((product, index) => (
                    <ProductCard key={product.id} product={product} index={index} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-white border border-[#E8E4DE]">
                  <p className="font-['Cormorant_Garamond'] text-2xl text-[#8B7355] mb-4">No products found</p>
                  <p className="font-['Montserrat'] text-sm text-[#5A6B5D] mb-6">Try adjusting your filters</p>
                  <Button 
                    onClick={clearFilters}
                    className="bg-[#2D4A3E] text-white hover:bg-[#1E332A] rounded-sm font-['Montserrat']"
                    data-testid="no-products-clear-filters"
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
